// ** Redux Imports
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { isNil } from 'lodash-es';

import { getErrorMessage } from '@/utils/errors';

import { RootState } from '../index';

const DEBUG = false;
// NOTE: a distinct service from the sports `statsApiUrl` — this is the Owners Hub
// aggregations API (`/api/wl-owner-hub-stats/v1`). See `public/assets/config/ini.js`.
const apiUrl = window.config.ownerHubStatsApiUrl + '/aggregations';
// The same service's ROW-level namespace: `/select/data-set` returns raw records
// (not aggregations) — today only the `players` roster. See the player-list section.
const selectApiUrl = window.config.ownerHubStatsApiUrl + '/select';

type GenericError = {
  rejectValue: {
    error: string;
  };
};

const getAuthHeaders = (state: RootState): Record<string, string> | null => {
  const authToken = state.authentication.access_token;
  if (isNil(authToken)) return null;
  return { Authorization: authToken };
};

/* ---------- API contract ----------

Verified live against `micros-spark-test1` (see
`docs/api.owners-hub.stats.md`). The window is driven by `start_date` / `end_date`
(YYYY-MM-DD); the range is **end-exclusive** (`start_date <= row < end_date`).

  GET  {ownerHubStatsApiUrl}/aggregations/data-sets
       → [{ name, aggregate_on: string[], data_points: string[] }]

  POST {ownerHubStatsApiUrl}/aggregations/data-set
       { casino_instance_id, data_set, start_date, end_date?, aggregate_on?, filter? }
       → one aggregation over [start_date, end_date). For a data set with no
         dimensions the body is the flat metrics object; with dimensions it is
         `{ aggregate_on, data_points }`.

  POST {ownerHubStatsApiUrl}/aggregations/data-set-history
       { casino_instance_id, data_set, start_date, end_date?, interval_slice, aggregate_on?, filter? }
       → [{ date, data: [<group>] }] — one bucket per `interval_slice`. Each group
         is the flat metrics object (no dimensions) or `{ aggregate_on, data_points }`.

Auth note: a token that is valid but **not authorized for the casino** returns
`500` (not 401/403), so it does not trip the global auth-interceptor. A genuinely
bad token returns `401`.

The aggregation payloads are stored RAW (`unknown`) for the consumer (see
`pages/home/useHomeStats.ts`) to shape. Only the list endpoint is normalised.
*/

/** One entry from `/aggregations/data-sets`. */
export type StatsDataSetInfo =
  | string
  | { name?: string; data_set?: string; aggregate_on?: string[]; data_points?: string[]; [key: string]: unknown };

/** Raw aggregation payload from `/aggregations/data-set`. */
export type StatsAggregation = unknown;

/** Raw time-sliced payload from `/aggregations/data-set-history`. */
export type StatsHistory = unknown;

export interface StatsAggregationEntry {
  casinoInstanceId: string;
  dataSet: string;
  startDate: string;
  endDate: string;
  data: StatsAggregation;
}

export interface StatsHistoryEntry {
  casinoInstanceId: string;
  dataSet: string;
  startDate: string;
  endDate: string;
  intervalSlice: string;
  data: StatsHistory;
}

/** A query identity for a single aggregation (brand + data set + window + grouping). */
export type StatsDataSetRef = {
  casinoInstanceId: string;
  dataSet: string;
  startDate: string;
  endDate: string;
  /** Dimensions grouped on — part of the identity (different groupings ≠ same slot). */
  aggregateOn?: string[];
  /** Dimension filters — part of the identity (e.g. one card per `aft` value). */
  filter?: Record<string, string>;
  /** `order_by` clause (e.g. `{ casino_bet_amount: 'desc' }`); part of the identity. */
  orderBy?: Record<string, StatsOrderDir>;
  /** Row cap (server `max_limit` is 50); part of the identity. */
  limit?: number;
  /** Pagination offset; part of the identity. */
  offset?: number;
};
/** A query identity for a time-sliced aggregation (adds the bucket size). */
export type StatsHistoryRef = StatsDataSetRef & { intervalSlice: string };

// Result/loading maps are keyed by the FULL request identity — including the
// window (start/end), slice AND the grouping/filter — NOT just brand+dataSet. This
// makes each window cache independently, so switching ranges back and forth reuses
// prior results and an out-of-order response for an old window can never overwrite
// the current one. Including the grouping/filter is essential for the per-channel
// Acquisition cards: same data set + window, different `aft` filter must NOT share
// a cache slot (else the cards clobber each other).
/** Sort direction for an `order_by` clause. */
export type StatsOrderDir = 'asc' | 'desc';

/** Stable cache-suffix for a query's optional `aggregate_on` / `filter` / `order_by` /
 *  pagination: all part of a query's identity so distinct sorts/pages cache apart. */
export const statsQueryKey = (
  aggregateOn?: readonly string[],
  filter?: Record<string, string>,
  orderBy?: Record<string, StatsOrderDir>,
  limit?: number,
  offset?: number,
): string => {
  const agg = (aggregateOn ?? []).join(',');
  const flt = filter
    ? Object.keys(filter)
        .sort()
        .map((k) => `${k}=${filter[k]}`)
        .join('&')
    : '';
  const ord = orderBy
    ? Object.keys(orderBy)
        .sort()
        .map((k) => `${k}:${orderBy[k]}`)
        .join('&')
    : '';
  const pag = limit !== undefined || offset !== undefined ? `${limit ?? ''}:${offset ?? ''}` : '';
  return `${agg}#${flt}#${ord}#${pag}`;
};
export const statsDataSetKey = (
  casinoInstanceId: string,
  dataSet: string,
  startDate: string,
  endDate: string,
  queryKey = '',
): string => `${casinoInstanceId}|${dataSet}|${startDate}|${endDate}|${queryKey}`;
export const statsHistoryKey = (
  casinoInstanceId: string,
  dataSet: string,
  startDate: string,
  endDate: string,
  intervalSlice: string,
  queryKey = '',
): string => `${casinoInstanceId}|${dataSet}|${startDate}|${endDate}|${intervalSlice}|${queryKey}`;

const toDataSetArray = (payload: any): StatsDataSetInfo[] => {
  if (Array.isArray(payload)) return payload as StatsDataSetInfo[];
  if (payload && Array.isArray(payload.data)) return payload.data as StatsDataSetInfo[];
  if (payload && Array.isArray(payload.data_sets)) return payload.data_sets as StatsDataSetInfo[];
  return [];
};

/** Optional grouping/filtering/ordering common to both POST endpoints. */
type AggregationQuery = {
  /** Dimensions to group by (subset of the data set's `aggregate_on`). */
  aggregateOn?: string[];
  /** `{ dimension: value }` filters, e.g. `{ campaign: '' }`. */
  filter?: Record<string, string>;
  /** Server-side sort, e.g. `{ casino_bet_amount: 'desc' }` (single field honoured). */
  orderBy?: Record<string, StatsOrderDir>;
  /** Cap the number of rows returned (server `max_limit` is 50). */
  limit?: number;
  /** Pagination offset for `limit`-paged data sets (e.g. `daily_player_activity`). */
  offset?: number;
};

/** Build the shared request body for the POST aggregation endpoints. */
const buildAggregationBody = (
  casinoInstanceId: string,
  dataSet: string,
  startDate: string,
  endDate: string | undefined,
  query: AggregationQuery,
): Record<string, unknown> => ({
  casino_instance_id: casinoInstanceId,
  data_set: dataSet,
  start_date: startDate,
  ...(endDate ? { end_date: endDate } : {}),
  ...(query.aggregateOn ? { aggregate_on: query.aggregateOn } : {}),
  ...(query.filter ? { filter: query.filter } : {}),
  ...(query.orderBy ? { order_by: query.orderBy } : {}),
  ...(query.limit !== undefined ? { limit: query.limit } : {}),
  ...(query.offset !== undefined ? { offset: query.offset } : {}),
});

/* ---------- thunks ---------- */

/** GET the catalogue of data sets the user can query. */
export const fetchStatsDataSets = createAsyncThunk<StatsDataSetInfo[], void, GenericError>(
  'ownerHubStats/dataSets',
  async (_, { rejectWithValue, getState }) => {
    try {
      const headers = getAuthHeaders(getState() as RootState);
      if (!headers) return rejectWithValue({ error: 'No authentication token found' });

      const response = await axios.get(`${apiUrl}/data-sets`, { headers });
      DEBUG && console.log('[OwnerHubStats Slice] data-sets response', response);

      if (!isNil(response.data)) return toDataSetArray(response.data);
      return rejectWithValue({ error: "Couldn't fetch stats data sets" });
    } catch (error: any) {
      return rejectWithValue({ error: getErrorMessage(error) });
    }
  },
);

export type FetchStatsDataSetProps = AggregationQuery & {
  casinoInstanceId: string;
  dataSet: string;
  /** Window start (inclusive), `YYYY-MM-DD`. */
  startDate: string;
  /** Window end (EXCLUSIVE), `YYYY-MM-DD`. Optional. */
  endDate?: string;
};

/** POST a single aggregation for a brand + data set over `[start_date, end_date)`. */
export const fetchStatsDataSet = createAsyncThunk<StatsAggregationEntry, FetchStatsDataSetProps, GenericError>(
  'ownerHubStats/dataSet',
  async ({ casinoInstanceId, dataSet, startDate, endDate, aggregateOn, filter, orderBy, limit, offset }, { rejectWithValue, getState }) => {
    try {
      const headers = getAuthHeaders(getState() as RootState);
      if (!headers) return rejectWithValue({ error: 'No authentication token found' });
      if (isNil(casinoInstanceId) || isNil(dataSet) || isNil(startDate)) {
        return rejectWithValue({ error: 'casinoInstanceId, dataSet and startDate are required' });
      }

      const response = await axios.post(
        `${apiUrl}/data-set`,
        buildAggregationBody(casinoInstanceId, dataSet, startDate, endDate, { aggregateOn, filter, orderBy, limit, offset }),
        { headers },
      );
      DEBUG && console.log('[OwnerHubStats Slice] data-set response', response);

      if (!isNil(response.data)) return { casinoInstanceId, dataSet, startDate, endDate: endDate ?? '', data: response.data };
      return rejectWithValue({ error: "Couldn't fetch stats data set" });
    } catch (error: any) {
      return rejectWithValue({ error: getErrorMessage(error) });
    }
  },
);

/* ---------- player roster (fetch-all) ---------- */

// `daily_player_activity` (one row per player, keyed by `email`) has NO server-side
// email search, NO string/email sort (a string `order_by` 500s) and returns NO total
// count — just a numerically-orderable, offset-paged array (server page max 50).
// Verified live against `micros-spark-test1`. So to power the Players page's email
// search + arbitrary sort + true pagination we page the WHOLE roster into memory once
// per (brand, window); the page then filters/sorts/paginates client-side, exactly as
// it did over the mock array. Capped so a pathologically large brand can't loop forever.
//
// SHAPE QUIRK (verified live): the body is `[]` for no players and a bare array for
// 2+, but a SINGLE player collapses to the flat row OBJECT (not a 1-element array).
// So a 1-player page must be UNWRAPPED, never mistaken for an empty/error result —
// see `isPlayerActivityRow` / `playerActivityRows`. (Players with zero activity still
// appear: a registered account with no bets or deposits is a valid row.)
const PLAYER_ROSTER_DATA_SET = 'daily_player_activity';
const PLAYER_ROSTER_PAGE = 50; // server `max_limit`
const PLAYER_ROSTER_MAX_PAGES = 40; // safety cap → up to 2000 players
// Fetch the heaviest wagerers first so a truncated roster keeps the most relevant rows.
const PLAYER_ROSTER_ORDER: Record<string, StatsOrderDir> = { casino_bet_amount: 'desc' };

/** A single `daily_player_activity` row, identified by its string `email` — as
 *  distinct from an error/empty body (e.g. `{ error_uid, message }` or `[]`). */
const isPlayerActivityRow = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && typeof (value as { email?: unknown }).email === 'string';

/**
 * Normalize a `daily_player_activity` aggregation body to an array of rows, absorbing
 * the single-row collapse above: array → as-is; lone player object → `[row]`;
 * empty/error body → `[]`. Every per-player consumer goes through this (the roster
 * fetch-all and the Stats "Top Players" board) so a 1-player brand renders, not errors.
 */
export const playerActivityRows = (data: unknown): Record<string, unknown>[] =>
  Array.isArray(data) ? (data as Record<string, unknown>[]) : isPlayerActivityRow(data) ? [data] : [];

export interface PlayerRosterEntry {
  casinoInstanceId: string;
  startDate: string;
  endDate: string;
  /** Raw rows concatenated across every page (the consumer maps/scales them). */
  data: unknown[];
  /** Hit the page cap with a still-full page — more players exist than were fetched. */
  truncated: boolean;
}

/** A query identity for one brand's full roster over a window. */
export type PlayerRosterRef = { casinoInstanceId: string; startDate: string; endDate: string };

export const playerRosterKey = (casinoInstanceId: string, startDate: string, endDate: string): string =>
  `${casinoInstanceId}|${startDate}|${endDate}`;

export type FetchAllPlayerActivityProps = {
  casinoInstanceId: string;
  /** Window start (inclusive), `YYYY-MM-DD`. */
  startDate: string;
  /** Window end (EXCLUSIVE), `YYYY-MM-DD`. Optional. */
  endDate?: string;
};

/** Page `daily_player_activity` to completion (or the cap) for one brand + window. */
export const fetchAllPlayerActivity = createAsyncThunk<PlayerRosterEntry, FetchAllPlayerActivityProps, GenericError>(
  'ownerHubStats/playerRoster',
  async ({ casinoInstanceId, startDate, endDate }, { rejectWithValue, getState }) => {
    try {
      const headers = getAuthHeaders(getState() as RootState);
      if (!headers) return rejectWithValue({ error: 'No authentication token found' });
      if (isNil(casinoInstanceId) || isNil(startDate)) {
        return rejectWithValue({ error: 'casinoInstanceId and startDate are required' });
      }

      const rows: unknown[] = [];
      let truncated = false;
      for (let page = 0; page < PLAYER_ROSTER_MAX_PAGES; page += 1) {
        const body = buildAggregationBody(casinoInstanceId, PLAYER_ROSTER_DATA_SET, startDate, endDate, {
          orderBy: PLAYER_ROSTER_ORDER,
          limit: PLAYER_ROSTER_PAGE,
          offset: page * PLAYER_ROSTER_PAGE,
        });
        // eslint-disable-next-line no-await-in-loop -- offset paging is inherently sequential.
        const response = await axios.post(`${apiUrl}/data-set`, body, { headers });
        // An array is the normal page; a lone player object is the single-row collapse
        // (1 player left on this page) → unwrap it. Anything else non-array is a genuine
        // error payload (e.g. brand not authorized → `{ error_uid, message }` with a 200
        // body): fail hard on the first page; on a LATER page keep the rows we have but
        // mark the roster PARTIAL so an incomplete list is never shown as complete.
        let pageRows: unknown[];
        if (Array.isArray(response.data)) {
          pageRows = response.data;
        } else if (isPlayerActivityRow(response.data)) {
          pageRows = [response.data];
        } else {
          if (page === 0) return rejectWithValue({ error: "Couldn't fetch player roster" });
          truncated = true;
          break;
        }
        rows.push(...pageRows);
        if (pageRows.length < PLAYER_ROSTER_PAGE) break; // short page → last page
        if (page === PLAYER_ROSTER_MAX_PAGES - 1) truncated = true; // full page at the cap
      }
      DEBUG && console.log('[OwnerHubStats Slice] player roster rows', rows.length, 'truncated', truncated);

      return { casinoInstanceId, startDate, endDate: endDate ?? '', data: rows, truncated };
    } catch (error: any) {
      return rejectWithValue({ error: getErrorMessage(error) });
    }
  },
  {
    // Paging a roster is up to 40 sequential requests, and the consumer's effect can
    // only see the CACHED entry — which stays undefined for the whole sweep. Without
    // this, leaving the page and coming back mid-load starts a second identical
    // sweep. A skipped dispatch is silent (no rejected action), so a failed load
    // still retries on the next visit, exactly as before.
    condition: ({ casinoInstanceId, startDate, endDate }, { getState }) =>
      !(getState() as RootState).ownerHubStats.playerRosterLoadingByKey[
        playerRosterKey(casinoInstanceId, startDate, endDate ?? '')
      ],
  },
);

/* ---------- player list (fetch-all, `/select/data-set`) ---------- */

// `/select/data-set` is a ROW-level (non-aggregating) sibling of `/aggregations/
// data-set` on the same service. Its `players` data set is the brand's FULL
// registration roster — every account, including the ~98% with no transactions —
// whereas `daily_player_activity` only has rows for players who actually wagered or
// deposited (verified live on `micros-spark-test1`: 217 vs 5 for the same brand).
// So `players` is the base list and the activity data set ENRICHES it; see
// `pages/players/usePlayersRoster.ts` for the merge.
//
// Verified contract (live, 2026-08-03 — full write-up in `docs/api.owners-hub.stats.md`):
//   · required `casino_instance_id` + `data_set`; NO `start_date` (it is not a window
//     query — `start_date`/`end_date` are accepted but ignored)
//   · `limit` defaults to 10 and is CAPPED at 50 server-side (asking for 500 yields 50)
//   · `offset` pages it; past the end returns `[]`
//   · `order_by` takes any real column INCLUDING strings (`{ email: 'asc' }` works —
//     unlike the aggregations endpoint, where a string sort 500s); unknown column → 500
//   · `filter` is exact-match equality, AND-ed across keys; unknown column → 500, and
//     JSON `null` does NOT match SQL NULL (so "unattributed" can't be filtered server-side)
//   · the body is ALWAYS a JSON array — a 1-row result stays `[row]`, so this data set
//     has none of `daily_player_activity`'s single-row-collapse quirk
const PLAYER_LIST_DATA_SET = 'players';
const PLAYER_LIST_PAGE = 50; // server `max_limit`
const PLAYER_LIST_MAX_PAGES = 40; // safety cap → up to 2000 players
// Newest registrations first, so a truncated roster keeps the rows an owner most
// likely wants (and matches the page's default "Newest first" sort).
const PLAYER_LIST_ORDER: Record<string, StatsOrderDir> = { date_created_tz: 'desc' };

/** A single `players` row, identified by its string `email`, or by its `player_id` when the account has no email
 *  (a phone / social sign-up must still be listed). */
const isPlayerRow = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' &&
  value !== null &&
  (typeof (value as { email?: unknown }).email === 'string' ||
    typeof (value as { player_id?: unknown }).player_id === 'string');

export interface PlayerListEntry {
  casinoInstanceId: string;
  /** Raw rows concatenated across every page (the consumer maps them). */
  data: unknown[];
  /** Hit the page cap with a still-full page — more players exist than were fetched. */
  truncated: boolean;
}

export type FetchAllPlayersProps = { casinoInstanceId: string };

/**
 * Page the `players` data set to completion (or the cap) for one brand.
 *
 * Unlike the activity roster there is NO date window: `players` is the registration
 * roster as of now, so the cache is keyed by brand alone.
 */
export const fetchAllPlayers = createAsyncThunk<PlayerListEntry, FetchAllPlayersProps, GenericError>(
  'ownerHubStats/playerList',
  async ({ casinoInstanceId }, { rejectWithValue, getState }) => {
    try {
      const headers = getAuthHeaders(getState() as RootState);
      if (!headers) return rejectWithValue({ error: 'No authentication token found' });
      if (isNil(casinoInstanceId)) return rejectWithValue({ error: 'casinoInstanceId is required' });

      const rows: unknown[] = [];
      let truncated = false;
      for (let page = 0; page < PLAYER_LIST_MAX_PAGES; page += 1) {
        const body = {
          casino_instance_id: casinoInstanceId,
          data_set: PLAYER_LIST_DATA_SET,
          order_by: PLAYER_LIST_ORDER,
          limit: PLAYER_LIST_PAGE,
          offset: page * PLAYER_LIST_PAGE,
        };
        // eslint-disable-next-line no-await-in-loop -- offset paging is inherently sequential.
        const response = await axios.post(`${selectApiUrl}/data-set`, body, { headers });
        // Always an array on success. Anything else is an error payload (e.g. brand not
        // authorized → `{ error_uid, message }`): fail hard on the first page; on a LATER
        // page keep what we have but mark the roster PARTIAL, so an incomplete list is
        // never presented as complete.
        if (!Array.isArray(response.data)) {
          if (page === 0) return rejectWithValue({ error: "Couldn't fetch player list" });
          truncated = true;
          break;
        }
        rows.push(...(response.data as unknown[]).filter(isPlayerRow));
        if (response.data.length < PLAYER_LIST_PAGE) break; // short page → last page
        if (page === PLAYER_LIST_MAX_PAGES - 1) truncated = true; // full page at the cap
      }
      DEBUG && console.log('[OwnerHubStats Slice] player list rows', rows.length, 'truncated', truncated);

      return { casinoInstanceId, data: rows, truncated };
    } catch (error: any) {
      return rejectWithValue({ error: getErrorMessage(error) });
    }
  },
  {
    // Same in-flight guard as the activity roster above: the effect that dispatches
    // this can only see the cached entry, which is undefined until the whole sweep
    // finishes, so a remount mid-load would otherwise re-page the entire list.
    condition: ({ casinoInstanceId }, { getState }) =>
      !(getState() as RootState).ownerHubStats.playerListLoadingByKey[casinoInstanceId],
  },
);

export type FetchStatsDataSetHistoryProps = FetchStatsDataSetProps & {
  /** Bucket size to slice the window into, e.g. `'1 day'`, `'1 hour'`. */
  intervalSlice: string;
};

/** POST a time-sliced aggregation (one bucket per `interval_slice`). */
export const fetchStatsDataSetHistory = createAsyncThunk<StatsHistoryEntry, FetchStatsDataSetHistoryProps, GenericError>(
  'ownerHubStats/dataSetHistory',
  async (
    { casinoInstanceId, dataSet, startDate, endDate, intervalSlice, aggregateOn, filter },
    { rejectWithValue, getState },
  ) => {
    try {
      const headers = getAuthHeaders(getState() as RootState);
      if (!headers) return rejectWithValue({ error: 'No authentication token found' });
      if (isNil(casinoInstanceId) || isNil(dataSet) || isNil(startDate) || isNil(intervalSlice)) {
        return rejectWithValue({ error: 'casinoInstanceId, dataSet, startDate and intervalSlice are required' });
      }

      const response = await axios.post(
        `${apiUrl}/data-set-history`,
        {
          ...buildAggregationBody(casinoInstanceId, dataSet, startDate, endDate, { aggregateOn, filter }),
          interval_slice: intervalSlice,
        },
        { headers },
      );
      DEBUG && console.log('[OwnerHubStats Slice] data-set-history response', response);

      if (!isNil(response.data)) {
        return { casinoInstanceId, dataSet, startDate, endDate: endDate ?? '', intervalSlice, data: response.data };
      }
      return rejectWithValue({ error: "Couldn't fetch stats data set history" });
    } catch (error: any) {
      return rejectWithValue({ error: getErrorMessage(error) });
    }
  },
);

/* ---------- slice state ---------- */

export interface OwnerHubStatsState {
  /** Catalogue from `/aggregations/data-sets`. */
  dataSets: StatsDataSetInfo[];
  dataSetsLoading: boolean;
  dataSetsLoaded: boolean;
  dataSetsError: string | null | undefined;

  /** `/aggregations/data-set` results + per-request status, keyed by `statsDataSetKey`. */
  dataSetByKey: Record<string, StatsAggregationEntry>;
  dataSetLoadingByKey: Record<string, boolean>;
  dataSetErrorByKey: Record<string, string | undefined>;

  /** `/aggregations/data-set-history` results + per-request status, keyed by `statsHistoryKey`. */
  historyByKey: Record<string, StatsHistoryEntry>;
  historyLoadingByKey: Record<string, boolean>;
  historyErrorByKey: Record<string, string | undefined>;

  /** `daily_player_activity` full roster (fetch-all) + status, keyed by `playerRosterKey`. */
  playerRosterByKey: Record<string, PlayerRosterEntry>;
  playerRosterLoadingByKey: Record<string, boolean>;
  playerRosterErrorByKey: Record<string, string | undefined>;

  /** `select/players` full registration list (fetch-all) + status, keyed by brand id. */
  playerListByKey: Record<string, PlayerListEntry>;
  playerListLoadingByKey: Record<string, boolean>;
  playerListErrorByKey: Record<string, string | undefined>;
}

const initialState: OwnerHubStatsState = {
  dataSets: [],
  dataSetsLoading: false,
  dataSetsLoaded: false,
  dataSetsError: null,

  dataSetByKey: {},
  dataSetLoadingByKey: {},
  dataSetErrorByKey: {},

  historyByKey: {},
  historyLoadingByKey: {},
  historyErrorByKey: {},

  playerRosterByKey: {},
  playerRosterLoadingByKey: {},
  playerRosterErrorByKey: {},

  playerListByKey: {},
  playerListLoadingByKey: {},
  playerListErrorByKey: {},
};

/**
 * Is this slice still waiting for the fetch that is now settling?
 *
 * The two player fetch-alls page for up to 40 sequential requests, so they are the
 * app's longest in-flight work, and their payload is the most sensitive it holds
 * (emails, names, countries). A teardown (logout, or an impersonation identity
 * change) resets this slice to `initialState`, which clears the loading flag the
 * fetch set when it started. A sweep still running at that moment belongs to the
 * PREVIOUS identity: without this check it would repopulate the just-cleared cache
 * with that owner's player rows, and re-open a loading flag nobody is waiting on.
 *
 * The flag is the marker because only `pending` sets it and only the settling case
 * clears it, so "no longer true" means exactly "this slice was reset since we
 * started". No extra bookkeeping state is needed.
 */
const isStillAwaited = (loadingByKey: Record<string, boolean>, key: string): boolean => loadingByKey[key] === true;

export const ownerHubStatsSlice = createSlice({
  name: 'ownerHubStats',
  initialState,
  reducers: {
    // Wipe all cached per-brand stats on logout so one owner's figures never
    // survive into the next session in a shared browser (dispatched from the
    // central session-teardown listener in store/index.ts).
    resetOwnerHubStats: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      /* ----- GET /aggregations/data-sets ----- */
      .addCase(fetchStatsDataSets.pending, (state) => {
        state.dataSetsLoading = true;
        state.dataSetsLoaded = false;
        state.dataSetsError = null;
      })
      .addCase(fetchStatsDataSets.fulfilled, (state, action) => {
        state.dataSetsLoading = false;
        state.dataSetsLoaded = true;
        state.dataSets = action.payload;
      })
      .addCase(fetchStatsDataSets.rejected, (state, action) => {
        state.dataSetsLoading = false;
        state.dataSetsLoaded = true;
        state.dataSetsError = action.payload?.error;
      })

      /* ----- POST /aggregations/data-set ----- */
      // Keys derive from `action.meta.arg` (the original request) so the grouping/
      // filter are part of the identity — see `statsQueryKey`.
      .addCase(fetchStatsDataSet.pending, (state, action) => {
        const { casinoInstanceId, dataSet, startDate, endDate, aggregateOn, filter, orderBy, limit, offset } = action.meta.arg;
        const key = statsDataSetKey(casinoInstanceId, dataSet, startDate, endDate ?? '', statsQueryKey(aggregateOn, filter, orderBy, limit, offset));
        state.dataSetLoadingByKey[key] = true;
        state.dataSetErrorByKey[key] = undefined;
      })
      .addCase(fetchStatsDataSet.fulfilled, (state, action) => {
        const { casinoInstanceId, dataSet, startDate, endDate, aggregateOn, filter, orderBy, limit, offset } = action.meta.arg;
        const key = statsDataSetKey(casinoInstanceId, dataSet, startDate, endDate ?? '', statsQueryKey(aggregateOn, filter, orderBy, limit, offset));
        state.dataSetByKey[key] = action.payload;
        state.dataSetLoadingByKey[key] = false;
      })
      .addCase(fetchStatsDataSet.rejected, (state, action) => {
        const { casinoInstanceId, dataSet, startDate, endDate, aggregateOn, filter, orderBy, limit, offset } = action.meta.arg;
        const key = statsDataSetKey(casinoInstanceId, dataSet, startDate, endDate ?? '', statsQueryKey(aggregateOn, filter, orderBy, limit, offset));
        state.dataSetLoadingByKey[key] = false;
        state.dataSetErrorByKey[key] = action.payload?.error;
      })

      /* ----- POST /aggregations/data-set-history ----- */
      .addCase(fetchStatsDataSetHistory.pending, (state, action) => {
        const { casinoInstanceId, dataSet, startDate, endDate, intervalSlice, aggregateOn, filter } = action.meta.arg;
        const key = statsHistoryKey(casinoInstanceId, dataSet, startDate, endDate ?? '', intervalSlice, statsQueryKey(aggregateOn, filter));
        state.historyLoadingByKey[key] = true;
        state.historyErrorByKey[key] = undefined;
      })
      .addCase(fetchStatsDataSetHistory.fulfilled, (state, action) => {
        const { casinoInstanceId, dataSet, startDate, endDate, intervalSlice, aggregateOn, filter } = action.meta.arg;
        const key = statsHistoryKey(casinoInstanceId, dataSet, startDate, endDate ?? '', intervalSlice, statsQueryKey(aggregateOn, filter));
        state.historyByKey[key] = action.payload;
        state.historyLoadingByKey[key] = false;
      })
      .addCase(fetchStatsDataSetHistory.rejected, (state, action) => {
        const { casinoInstanceId, dataSet, startDate, endDate, intervalSlice, aggregateOn, filter } = action.meta.arg;
        const key = statsHistoryKey(casinoInstanceId, dataSet, startDate, endDate ?? '', intervalSlice, statsQueryKey(aggregateOn, filter));
        state.historyLoadingByKey[key] = false;
        state.historyErrorByKey[key] = action.payload?.error;
      })

      /* ----- player roster (fetch-all `daily_player_activity`) ----- */
      .addCase(fetchAllPlayerActivity.pending, (state, action) => {
        const { casinoInstanceId, startDate, endDate } = action.meta.arg;
        const key = playerRosterKey(casinoInstanceId, startDate, endDate ?? '');
        state.playerRosterLoadingByKey[key] = true;
        state.playerRosterErrorByKey[key] = undefined;
      })
      .addCase(fetchAllPlayerActivity.fulfilled, (state, action) => {
        const { casinoInstanceId, startDate, endDate } = action.meta.arg;
        const key = playerRosterKey(casinoInstanceId, startDate, endDate ?? '');
        if (!isStillAwaited(state.playerRosterLoadingByKey, key)) return;
        state.playerRosterByKey[key] = action.payload;
        state.playerRosterLoadingByKey[key] = false;
      })
      .addCase(fetchAllPlayerActivity.rejected, (state, action) => {
        const { casinoInstanceId, startDate, endDate } = action.meta.arg;
        const key = playerRosterKey(casinoInstanceId, startDate, endDate ?? '');
        if (!isStillAwaited(state.playerRosterLoadingByKey, key)) return;
        state.playerRosterLoadingByKey[key] = false;
        state.playerRosterErrorByKey[key] = action.payload?.error;
      })

      /* ----- player list (fetch-all `select/players`) ----- */
      // Keyed by brand alone: the `players` data set is not a window query.
      .addCase(fetchAllPlayers.pending, (state, action) => {
        const { casinoInstanceId } = action.meta.arg;
        state.playerListLoadingByKey[casinoInstanceId] = true;
        state.playerListErrorByKey[casinoInstanceId] = undefined;
      })
      .addCase(fetchAllPlayers.fulfilled, (state, action) => {
        const { casinoInstanceId } = action.meta.arg;
        if (!isStillAwaited(state.playerListLoadingByKey, casinoInstanceId)) return;
        state.playerListByKey[casinoInstanceId] = action.payload;
        state.playerListLoadingByKey[casinoInstanceId] = false;
      })
      .addCase(fetchAllPlayers.rejected, (state, action) => {
        const { casinoInstanceId } = action.meta.arg;
        if (!isStillAwaited(state.playerListLoadingByKey, casinoInstanceId)) return;
        state.playerListLoadingByKey[casinoInstanceId] = false;
        state.playerListErrorByKey[casinoInstanceId] = action.payload?.error;
      });
  },
});

/* ---------- selectors ---------- */

export const selectStatsDataSets = (state: RootState): StatsDataSetInfo[] => state.ownerHubStats.dataSets;

const dataSetKeyOf = (ref: StatsDataSetRef | undefined): string | undefined =>
  ref && !isNil(ref.casinoInstanceId) && !isNil(ref.dataSet) && !isNil(ref.startDate) && !isNil(ref.endDate)
    ? statsDataSetKey(
        ref.casinoInstanceId,
        ref.dataSet,
        ref.startDate,
        ref.endDate,
        statsQueryKey(ref.aggregateOn, ref.filter, ref.orderBy, ref.limit, ref.offset),
      )
    : undefined;

const historyKeyOf = (ref: StatsHistoryRef | undefined): string | undefined =>
  ref &&
  !isNil(ref.casinoInstanceId) &&
  !isNil(ref.dataSet) &&
  !isNil(ref.startDate) &&
  !isNil(ref.endDate) &&
  !isNil(ref.intervalSlice)
    ? statsHistoryKey(
        ref.casinoInstanceId,
        ref.dataSet,
        ref.startDate,
        ref.endDate,
        ref.intervalSlice,
        statsQueryKey(ref.aggregateOn, ref.filter),
      )
    : undefined;

export const selectStatsDataSet = (
  state: RootState,
  ref: StatsDataSetRef | undefined,
): StatsAggregationEntry | undefined => {
  const key = dataSetKeyOf(ref);
  return key ? state.ownerHubStats.dataSetByKey[key] : undefined;
};

export const selectStatsDataSetLoading = (state: RootState, ref: StatsDataSetRef | undefined): boolean => {
  const key = dataSetKeyOf(ref);
  return key ? !!state.ownerHubStats.dataSetLoadingByKey[key] : false;
};

export const selectStatsDataSetError = (state: RootState, ref: StatsDataSetRef | undefined): string | undefined => {
  const key = dataSetKeyOf(ref);
  return key ? state.ownerHubStats.dataSetErrorByKey[key] : undefined;
};

export const selectStatsDataSetHistory = (
  state: RootState,
  ref: StatsHistoryRef | undefined,
): StatsHistoryEntry | undefined => {
  const key = historyKeyOf(ref);
  return key ? state.ownerHubStats.historyByKey[key] : undefined;
};

export const selectStatsDataSetHistoryLoading = (state: RootState, ref: StatsHistoryRef | undefined): boolean => {
  const key = historyKeyOf(ref);
  return key ? !!state.ownerHubStats.historyLoadingByKey[key] : false;
};

export const selectStatsDataSetHistoryError = (
  state: RootState,
  ref: StatsHistoryRef | undefined,
): string | undefined => {
  const key = historyKeyOf(ref);
  return key ? state.ownerHubStats.historyErrorByKey[key] : undefined;
};

const rosterKeyOf = (ref: PlayerRosterRef | undefined): string | undefined =>
  ref && !isNil(ref.casinoInstanceId) && !isNil(ref.startDate) && !isNil(ref.endDate)
    ? playerRosterKey(ref.casinoInstanceId, ref.startDate, ref.endDate)
    : undefined;

export const selectPlayerRoster = (state: RootState, ref: PlayerRosterRef | undefined): PlayerRosterEntry | undefined => {
  const key = rosterKeyOf(ref);
  return key ? state.ownerHubStats.playerRosterByKey[key] : undefined;
};

export const selectPlayerRosterLoading = (state: RootState, ref: PlayerRosterRef | undefined): boolean => {
  const key = rosterKeyOf(ref);
  return key ? !!state.ownerHubStats.playerRosterLoadingByKey[key] : false;
};

export const selectPlayerRosterError = (state: RootState, ref: PlayerRosterRef | undefined): string | undefined => {
  const key = rosterKeyOf(ref);
  return key ? state.ownerHubStats.playerRosterErrorByKey[key] : undefined;
};

export const selectPlayerList = (state: RootState, casinoInstanceId: string | undefined): PlayerListEntry | undefined =>
  casinoInstanceId ? state.ownerHubStats.playerListByKey[casinoInstanceId] : undefined;

export const selectPlayerListLoading = (state: RootState, casinoInstanceId: string | undefined): boolean =>
  casinoInstanceId ? !!state.ownerHubStats.playerListLoadingByKey[casinoInstanceId] : false;

export const selectPlayerListError = (state: RootState, casinoInstanceId: string | undefined): string | undefined =>
  casinoInstanceId ? state.ownerHubStats.playerListErrorByKey[casinoInstanceId] : undefined;

export const { resetOwnerHubStats } = ownerHubStatsSlice.actions;

export default ownerHubStatsSlice.reducer;
